import {dissoc} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {SMART_SEARCH_KEY} from '../constants/smartSearchKey';
import {useFilterValues} from '../store/selectors';
import {useEagGridStore} from '../store/useEagGridStore';
import type {ExternalFilters} from '../types/ExternalFilters';
import type {FormToDataGridConnectorProps} from '../types/FormToDataGridConnectorProps';
import {unwrapFilterValues} from '../utils/unwrapFilterValues';
import {wrapFilterValues} from '../utils/wrapFilterValues';

/**
 * Bridges the EagGrid store filter state with external filter components.
 *
 * Returns `{values, onChange}` matching the `FormToDataGridConnectorProps` contract:
 * - `values`: flat `ExternalFilters` derived from the store's wrapped `filterValues`
 * - `onChange`: commits form-owned values back to the store while preserving smart search
 *
 * Used by both `CustomFiltersPanel` (sidebar) and `ExternalFiltersPortal` (portal)
 * to avoid duplicating store↔filter bridge logic.
 *
 */
export function useExternalFilterBridge(): FormToDataGridConnectorProps {
  const store = useEagGridStore();
  const filterValues = useFilterValues();

  const values = unwrapFilterValues(dissoc(SMART_SEARCH_KEY, filterValues)) as ExternalFilters;

  const onChange = (newValues: ExternalFilters) => {
    const currentSmartSearch = store.getState().filterValues[SMART_SEARCH_KEY];
    const nextFilterValues = wrapFilterValues(dissoc(SMART_SEARCH_KEY, newValues));

    if (isNotNil(currentSmartSearch)) {
      nextFilterValues[SMART_SEARCH_KEY] = currentSmartSearch;
    }

    store.setFilterValues(nextFilterValues);
  };

  return {values, onChange};
}
